import { Flex, Box, Checkbox, Text, TextInput, Select, TextArea, Card, ErrorBoundary, Spinner, Stack as Stack$1, Heading, Button, Code, ToastProvider, BoundaryElementProvider, PortalProvider, TabPanel, Layer, TabList, Tab, Breadcrumbs, MenuButton, Menu, MenuItem, Inline, Tree, TreeItem, Container } from '@sanity/ui';
import React, { createContext, useContext, memo, useMemo, useRef, useState, useCallback, useEffect, createElement, Suspense, Profiler } from 'react';
import pako from 'pako';
import axe from 'axe-core';
import { TrashIcon, PlayIcon, LaunchIcon, MoonIcon, SunIcon, SelectIcon, MenuIcon, ControlsIcon, SearchIcon } from '@sanity/icons';
import styled from 'styled-components';
import { set } from 'segmented-property';

const EMPTY_ARRAY = [];
const EMPTY_RECORD = {};
const DEFAULT_VIEWPORT_VALUE = 'auto';
const DEFAULT_ZOOM_VALUE = 1;
const VIEWPORT_OPTIONS = [{
  name: 'auto',
  title: 'Full',
  rect: {
    width: 'auto'
  }
}, {
  name: '768',
  title: '768px',
  rect: {
    width: 768
  }
}, {
  name: '375',
  title: '375px',
  rect: {
    width: 375,
    height: 667
  }
}, {
  name: '320',
  title: '320px',
  rect: {
    width: 320,
    height: 568
  }
}];
const ZOOM_OPTIONS = [{
  value: 0.5,
  title: '50%'
}, {
  value: 0.75,
  title: '75%'
}, {
  value: 1,
  title: '100%'
}, {
  value: 1.5,
  title: '150%'
}, {
  value: 2,
  title: '200%'
}, {
  value: 3,
  title: '300%'
}];

function defineConfig(config) {
  return config;
}
function defineScope(name, title, stories) {
  return {
    name,
    title,
    stories
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function createPubsub() {
  const subscribers = new Set();
  return {
    publish(msg) {
      for (const subscriber of subscribers) {
        subscriber(msg);
      }
    },

    subscribe(subscriber) {
      subscribers.add(subscriber);
      return () => {
        subscribers.delete(subscriber);
      };
    }

  };
}

const qs = {
  parse(str) {
    const params = new URLSearchParams('?' + str);
    const q = {};
    params.forEach((value, key) => {
      q[key] = value;
    });
    return q;
  },

  stringify(q) {
    return Object.entries(q).map(([key, value]) => `${key}=${value}`).join('&');
  }

};

function resolveLocation(scopes, path) {
  let segments = path == null ? void 0 : path.split('/').slice(1).filter(Boolean);
  const p = segments.join('/');

  if (segments.length === 0) {
    return {
      scope: null,
      story: null
    };
  }

  const scope = scopes.find(scope => p.startsWith(`${scope.name}/`)) || null;
  const len = (scope == null ? void 0 : scope.name.split('/').length) || 0;
  segments = segments.slice(len);
  const story = scope && scope.stories.find(story => story.name === segments.join('/')) || null;
  return {
    scope,
    story
  };
}
function buildFrameUrl(params) {
  const {
    baseUrl = '/frame/',
    path,
    payload,
    scheme,
    viewport,
    zoom
  } = params;
  return [baseUrl, `?path=${encodeURIComponent(path)}`, `&scheme=${scheme}`, `&viewport=${viewport}`, `&zoom=${zoom}`, ...Object.entries(payload).map(([key, value]) => {
    return `&${key}=${value}`;
  })].join('');
}

const PropsContext = /*#__PURE__*/createContext(null);

function useProps() {
  const props = useContext(PropsContext);

  if (!props) {
    throw new Error('Props: missing context value');
  }

  return props;
}

const BooleanProp = /*#__PURE__*/memo(function BooleanProp(props) {
  const {
    schema,
    value
  } = props;
  const {
    setPropValue
  } = useProps();
  return /*#__PURE__*/React.createElement(Flex, {
    as: "label",
    padding: 3
  }, /*#__PURE__*/React.createElement(Box, {
    marginRight: 2,
    style: {
      lineHeight: 0
    }
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: value || false,
    onChange: event => setPropValue(schema.name, event.currentTarget.checked)
  })), /*#__PURE__*/React.createElement(Box, {
    paddingY: 1
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name)));
});

const NumberProp = /*#__PURE__*/memo(function NumberProp(props) {
  const {
    schema,
    value = ''
  } = props;
  const {
    setPropValue
  } = useProps();
  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name), /*#__PURE__*/React.createElement(Box, {
    marginTop: 2
  }, /*#__PURE__*/React.createElement(TextInput, {
    fontSize: 1,
    onChange: event => setPropValue(schema.name, Number(event.currentTarget.value)),
    padding: 2,
    value: value
  })));
});

const SelectProp = /*#__PURE__*/memo(function SelectProp(props) {
  const {
    schema,
    value: valueProp
  } = props;
  const {
    setPropValue
  } = useProps();
  const value = useMemo(() => {
    const entries = Object.entries(schema.options);

    for (const [k, v] of entries) {
      if (v === valueProp) {
        return k;
      }
    }

    return '';
  }, [schema, valueProp]);
  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name), /*#__PURE__*/React.createElement(Box, {
    marginTop: 2
  }, /*#__PURE__*/React.createElement(Select, {
    fontSize: 1,
    onChange: event => {
      const optionKey = event.currentTarget.value;
      const optionValue = schema.options[optionKey];
      setPropValue(schema.name, optionValue);
    },
    padding: 2,
    radius: 2,
    value: String(value || '')
  }, Object.entries(schema.options).map(([key]) => /*#__PURE__*/React.createElement("option", {
    key: key,
    value: key
  }, key)))));
});

const StringProp = /*#__PURE__*/memo(function StringProp(props) {
  const {
    schema,
    value
  } = props;
  const {
    setPropValue
  } = useProps();
  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name), /*#__PURE__*/React.createElement(Box, {
    marginTop: 2
  }, /*#__PURE__*/React.createElement(TextInput, {
    fontSize: 1,
    onChange: event => setPropValue(schema.name, event.currentTarget.value),
    padding: 2,
    value: value || ''
  })));
});

const TextProp = /*#__PURE__*/memo(function TextProp(props) {
  const {
    schema,
    value
  } = props;
  const {
    setPropValue
  } = useProps();
  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name), /*#__PURE__*/React.createElement(Box, {
    marginTop: 2
  }, /*#__PURE__*/React.createElement(TextArea, {
    onChange: event => setPropValue(schema.name, event.currentTarget.value),
    rows: 4,
    value: value || ''
  })));
});

function Prop(props) {
  const {
    schema,
    value
  } = props;

  if (schema.type === 'boolean') {
    return /*#__PURE__*/React.createElement(BooleanProp, {
      schema: schema,
      value: value
    });
  }

  if (schema.type === 'number') {
    return /*#__PURE__*/React.createElement(NumberProp, {
      schema: schema,
      value: value
    });
  }

  if (schema.type === 'select') {
    return /*#__PURE__*/React.createElement(SelectProp, {
      schema: schema,
      value: value
    });
  }

  if (schema.type === 'string') {
    return /*#__PURE__*/React.createElement(StringProp, {
      schema: schema,
      value: value
    });
  }

  if (schema.type === 'text') {
    return /*#__PURE__*/React.createElement(TextProp, {
      schema: schema,
      value: value
    });
  }

  return /*#__PURE__*/React.createElement(Box, {
    padding: 2
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, "Unknown Prop type:", ' ', /*#__PURE__*/React.createElement("code", null, schema.name, ": ", schema.type)));
}

const PropsInspector = /*#__PURE__*/memo(function PropsInspector() {
  const {
    schemas,
    value
  } = useProps();
  return /*#__PURE__*/React.createElement(Box, {
    padding: 2
  }, schemas.length === 0 && /*#__PURE__*/React.createElement(Box, {
    padding: 2
  }, /*#__PURE__*/React.createElement(Text, {
    muted: true,
    size: 1
  }, "No properties")), schemas.length > 0 && schemas.map((schema, schemaIndex) => /*#__PURE__*/React.createElement(Prop, {
    key: schemaIndex,
    schema: schema,
    value: value[schema.name] === undefined ? schema.defaultValue : value[schema.name]
  })));
});

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || value !== value && other !== other;
}

var eq_1 = eq;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */

function assocIndexOf(array, key) {
  var length = array.length;

  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }

  return -1;
}

var _assocIndexOf = assocIndexOf;

/** Used for built-in method references. */

var arrayProto = Array.prototype;
/** Built-in value references. */

var splice = arrayProto.splice;
/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function listCacheDelete(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }

  var lastIndex = data.length - 1;

  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }

  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function listCacheGet(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);
  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */

function listCacheSet(key, value) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }

  return this;
}

var _listCacheSet = listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */

function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `ListCache`.


ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;
var _ListCache = ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */

function stackClear() {
  this.__data__ = new _ListCache();
  this.size = 0;
}

var _stackClear = stackClear;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);
  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn) {
  var module = { exports: {} };
	return fn(module, module.exports), module.exports;
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */

var freeSelf = typeof self == 'object' && self && self.Object === Object && self;
/** Used as a reference to the global object. */

var root = _freeGlobal || freeSelf || Function('return this')();
var _root = root;

/** Built-in value references. */

var Symbol = _root.Symbol;
var _Symbol = Symbol;

/** Used for built-in method references. */

var objectProto$b = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$8 = objectProto$b.hasOwnProperty;
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var nativeObjectToString$1 = objectProto$b.toString;
/** Built-in value references. */

var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;
/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */

function getRawTag(value) {
  var isOwn = hasOwnProperty$8.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);

  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }

  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var nativeObjectToString = objectProto$a.toString;
/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */

function objectToString(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */

var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';
/** Built-in value references. */

var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;
/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */

function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }

  return symToStringTag && symToStringTag in Object(value) ? _getRawTag(value) : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */

var asyncTag = '[object AsyncFunction]',
    funcTag$1 = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';
/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */

function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  } // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.


  var tag = _baseGetTag(value);
  return tag == funcTag$1 || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

/** Used to detect overreaching core-js shims. */

var coreJsData = _root['__core-js_shared__'];
var _coreJsData = coreJsData;

/** Used to detect methods masquerading as native. */

var maskSrcKey = function () {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? 'Symbol(src)_1.' + uid : '';
}();
/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */


function isMasked(func) {
  return !!maskSrcKey && maskSrcKey in func;
}

var _isMasked = isMasked;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype;
/** Used to resolve the decompiled source of functions. */

var funcToString$1 = funcProto$1.toString;
/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */

function toSource(func) {
  if (func != null) {
    try {
      return funcToString$1.call(func);
    } catch (e) {}

    try {
      return func + '';
    } catch (e) {}
  }

  return '';
}

var _toSource = toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */

var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;
/** Used to detect host constructors (Safari). */

var reIsHostCtor = /^\[object .+?Constructor\]$/;
/** Used for built-in method references. */

var funcProto = Function.prototype,
    objectProto$9 = Object.prototype;
/** Used to resolve the decompiled source of functions. */

var funcToString = funcProto.toString;
/** Used to check objects for own properties. */

var hasOwnProperty$7 = objectProto$9.hasOwnProperty;
/** Used to detect if a method is native. */

var reIsNative = RegExp('^' + funcToString.call(hasOwnProperty$7).replace(reRegExpChar, '\\$&').replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$');
/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */

function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }

  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}

var _baseIsNative = baseIsNative;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */

function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}

var _getNative = getNative;

/* Built-in method references that are verified to be native. */

var Map = _getNative(_root, 'Map');
var _Map = Map;

/* Built-in method references that are verified to be native. */

var nativeCreate = _getNative(Object, 'create');
var _nativeCreate = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */

function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}

var _hashClear = hashClear;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete;

/** Used to stand-in for `undefined` hash values. */

var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';
/** Used for built-in method references. */

var objectProto$8 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$6 = objectProto$8.hasOwnProperty;
/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function hashGet(key) {
  var data = this.__data__;

  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED$2 ? undefined : result;
  }

  return hasOwnProperty$6.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet;

/** Used for built-in method references. */

var objectProto$7 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$5 = objectProto$7.hasOwnProperty;
/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? data[key] !== undefined : hasOwnProperty$5.call(data, key);
}

var _hashHas = hashHas;

/** Used to stand-in for `undefined` hash values. */

var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';
/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */

function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = _nativeCreate && value === undefined ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */

function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `Hash`.


Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;
var _Hash = Hash;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */

function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash(),
    'map': new (_Map || _ListCache)(),
    'string': new _Hash()
  };
}

var _mapCacheClear = mapCacheClear;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean' ? value !== '__proto__' : value === null;
}

var _isKeyable = isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */

function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key) ? data[typeof key == 'string' ? 'string' : 'hash'] : data.map;
}

var _getMapData = getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}

var _mapCacheGet = mapCacheGet;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}

var _mapCacheHas = mapCacheHas;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */

function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
      size = data.size;
  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */

function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `MapCache`.


MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;
var _MapCache = MapCache;

/** Used as the size to enable large array optimizations. */

var LARGE_ARRAY_SIZE = 200;
/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */

function stackSet(key, value) {
  var data = this.__data__;

  if (data instanceof _ListCache) {
    var pairs = data.__data__;

    if (!_Map || pairs.length < LARGE_ARRAY_SIZE - 1) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }

    data = this.__data__ = new _MapCache(pairs);
  }

  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */

function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
} // Add methods to `Stack`.


Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;
var _Stack = Stack;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';
/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */

function setCacheAdd(value) {
  this.__data__.set(value, HASH_UNDEFINED);

  return this;
}

var _setCacheAdd = setCacheAdd;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */
function setCacheHas(value) {
  return this.__data__.has(value);
}

var _setCacheHas = setCacheHas;

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */

function SetCache(values) {
  var index = -1,
      length = values == null ? 0 : values.length;
  this.__data__ = new _MapCache();

  while (++index < length) {
    this.add(values[index]);
  }
} // Add methods to `SetCache`.


SetCache.prototype.add = SetCache.prototype.push = _setCacheAdd;
SetCache.prototype.has = _setCacheHas;
var _SetCache = SetCache;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }

  return false;
}

var _arraySome = arraySome;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function cacheHas(cache, key) {
  return cache.has(key);
}

var _cacheHas = cacheHas;

/** Used to compose bitmasks for value comparisons. */

var COMPARE_PARTIAL_FLAG$3 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;
/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */

function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$3,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  } // Check that cyclic values are equal.


  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);

  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }

  var index = -1,
      result = true,
      seen = bitmask & COMPARE_UNORDERED_FLAG$1 ? new _SetCache() : undefined;
  stack.set(array, other);
  stack.set(other, array); // Ignore non-index properties.

  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial ? customizer(othValue, arrValue, index, other, array, stack) : customizer(arrValue, othValue, index, array, other, stack);
    }

    if (compared !== undefined) {
      if (compared) {
        continue;
      }

      result = false;
      break;
    } // Recursively compare arrays (susceptible to call stack limits).


    if (seen) {
      if (!_arraySome(other, function (othValue, othIndex) {
        if (!_cacheHas(seen, othIndex) && (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
          return seen.push(othIndex);
        }
      })) {
        result = false;
        break;
      }
    } else if (!(arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
      result = false;
      break;
    }
  }

  stack['delete'](array);
  stack['delete'](other);
  return result;
}

var _equalArrays = equalArrays;

/** Built-in value references. */

var Uint8Array$1 = _root.Uint8Array;
var _Uint8Array = Uint8Array$1;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */
function mapToArray(map) {
  var index = -1,
      result = Array(map.size);
  map.forEach(function (value, key) {
    result[++index] = [key, value];
  });
  return result;
}

var _mapToArray = mapToArray;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */
function setToArray(set) {
  var index = -1,
      result = Array(set.size);
  set.forEach(function (value) {
    result[++index] = value;
  });
  return result;
}

var _setToArray = setToArray;

/** Used to compose bitmasks for value comparisons. */

var COMPARE_PARTIAL_FLAG$2 = 1,
    COMPARE_UNORDERED_FLAG = 2;
/** `Object#toString` result references. */

var boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    errorTag$1 = '[object Error]',
    mapTag$2 = '[object Map]',
    numberTag$1 = '[object Number]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag$1 = '[object Symbol]';
var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]';
/** Used to convert symbols to primitives and strings. */

var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;
/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag$2:
      if (object.byteLength != other.byteLength || object.byteOffset != other.byteOffset) {
        return false;
      }

      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag$1:
      if (object.byteLength != other.byteLength || !equalFunc(new _Uint8Array(object), new _Uint8Array(other))) {
        return false;
      }

      return true;

    case boolTag$1:
    case dateTag$1:
    case numberTag$1:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq_1(+object, +other);

    case errorTag$1:
      return object.name == other.name && object.message == other.message;

    case regexpTag$1:
    case stringTag$1:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == other + '';

    case mapTag$2:
      var convert = _mapToArray;

    case setTag$2:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$2;
      convert || (convert = _setToArray);

      if (object.size != other.size && !isPartial) {
        return false;
      } // Assume cyclic values are equal.


      var stacked = stack.get(object);

      if (stacked) {
        return stacked == other;
      }

      bitmask |= COMPARE_UNORDERED_FLAG; // Recursively compare objects (susceptible to call stack limits).

      stack.set(object, other);
      var result = _equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag$1:
      if (symbolValueOf) {
        return symbolValueOf.call(object) == symbolValueOf.call(other);
      }

  }

  return false;
}

var _equalByTag = equalByTag;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }

  return array;
}

var _arrayPush = arrayPush;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray$1 = Array.isArray;
var isArray_1 = isArray$1;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */

function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];

    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }

  return result;
}

var _arrayFilter = arrayFilter;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

var stubArray_1 = stubArray;

/** Used for built-in method references. */

var objectProto$6 = Object.prototype;
/** Built-in value references. */

var propertyIsEnumerable$1 = objectProto$6.propertyIsEnumerable;
/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeGetSymbols = Object.getOwnPropertySymbols;
/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */

var getSymbols = !nativeGetSymbols ? stubArray_1 : function (object) {
  if (object == null) {
    return [];
  }

  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function (symbol) {
    return propertyIsEnumerable$1.call(object, symbol);
  });
};
var _getSymbols = getSymbols;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }

  return result;
}

var _baseTimes = baseTimes;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */

var argsTag$2 = '[object Arguments]';
/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */

function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag$2;
}

var _baseIsArguments = baseIsArguments;

/** Used for built-in method references. */

var objectProto$5 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$4 = objectProto$5.hasOwnProperty;
/** Built-in value references. */

var propertyIsEnumerable = objectProto$5.propertyIsEnumerable;
/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */

var isArguments = _baseIsArguments(function () {
  return arguments;
}()) ? _baseIsArguments : function (value) {
  return isObjectLike_1(value) && hasOwnProperty$4.call(value, 'callee') && !propertyIsEnumerable.call(value, 'callee');
};
var isArguments_1 = isArguments;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

var isBuffer_1 = createCommonjsModule(function (module, exports) {
  /** Detect free variable `exports`. */
  var freeExports = exports && !exports.nodeType && exports;
  /** Detect free variable `module`. */

  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
  /** Detect the popular CommonJS extension `module.exports`. */

  var moduleExports = freeModule && freeModule.exports === freeExports;
  /** Built-in value references. */

  var Buffer = moduleExports ? _root.Buffer : undefined;
  /* Built-in method references for those with the same name as other `lodash` methods. */

  var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;
  /**
   * Checks if `value` is a buffer.
   *
   * @static
   * @memberOf _
   * @since 4.3.0
   * @category Lang
   * @param {*} value The value to check.
   * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
   * @example
   *
   * _.isBuffer(new Buffer(2));
   * // => true
   *
   * _.isBuffer(new Uint8Array(2));
   * // => false
   */

  var isBuffer = nativeIsBuffer || stubFalse_1;
  module.exports = isBuffer;
});

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;
/** Used to detect unsigned integer values. */

var reIsUint = /^(?:0|[1-9]\d*)$/;
/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */

function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$1 : length;
  return !!length && (type == 'number' || type != 'symbol' && reIsUint.test(value)) && value > -1 && value % 1 == 0 && value < length;
}

var _isIndex = isIndex;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;
/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */

function isLength(value) {
  return typeof value == 'number' && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

var isLength_1 = isLength;

/** `Object#toString` result references. */

var argsTag$1 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag = '[object Function]',
    mapTag$1 = '[object Map]',
    numberTag = '[object Number]',
    objectTag$2 = '[object Object]',
    regexpTag = '[object RegExp]',
    setTag$1 = '[object Set]',
    stringTag = '[object String]',
    weakMapTag$1 = '[object WeakMap]';
var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag$1 = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';
/** Used to identify `toStringTag` values of typed arrays. */

var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] = typedArrayTags[int8Tag] = typedArrayTags[int16Tag] = typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] = typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] = typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag$1] = typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] = typedArrayTags[dataViewTag$1] = typedArrayTags[dateTag] = typedArrayTags[errorTag] = typedArrayTags[funcTag] = typedArrayTags[mapTag$1] = typedArrayTags[numberTag] = typedArrayTags[objectTag$2] = typedArrayTags[regexpTag] = typedArrayTags[setTag$1] = typedArrayTags[stringTag] = typedArrayTags[weakMapTag$1] = false;
/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */

function baseIsTypedArray(value) {
  return isObjectLike_1(value) && isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}

var _baseIsTypedArray = baseIsTypedArray;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function (value) {
    return func(value);
  };
}

var _baseUnary = baseUnary;

var _nodeUtil = createCommonjsModule(function (module, exports) {
  /** Detect free variable `exports`. */
  var freeExports = exports && !exports.nodeType && exports;
  /** Detect free variable `module`. */

  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
  /** Detect the popular CommonJS extension `module.exports`. */

  var moduleExports = freeModule && freeModule.exports === freeExports;
  /** Detect free variable `process` from Node.js. */

  var freeProcess = moduleExports && _freeGlobal.process;
  /** Used to access faster Node.js helpers. */

  var nodeUtil = function () {
    try {
      // Use `util.types` for Node.js 10+.
      var types = freeModule && freeModule.require && freeModule.require('util').types;

      if (types) {
        return types;
      } // Legacy `process.binding('util')` for Node.js < 10.


      return freeProcess && freeProcess.binding && freeProcess.binding('util');
    } catch (e) {}
  }();

  module.exports = nodeUtil;
});

/* Node.js helper references. */

var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;
/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */

var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;
var isTypedArray_1 = isTypedArray;

/** Used for built-in method references. */

var objectProto$4 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$3 = objectProto$4.hasOwnProperty;
/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */

function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
      isArg = !isArr && isArguments_1(value),
      isBuff = !isArr && !isArg && isBuffer_1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? _baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$3.call(value, key)) && !(skipIndexes && ( // Safari 9 has enumerable `arguments.length` in strict mode.
    key == 'length' || // Node.js 0.10 has enumerable non-index properties on buffers.
    isBuff && (key == 'offset' || key == 'parent') || // PhantomJS 2 has enumerable non-index properties on typed arrays.
    isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset') || // Skip index properties.
    _isIndex(key, length)))) {
      result.push(key);
    }
  }

  return result;
}

var _arrayLikeKeys = arrayLikeKeys;

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;
/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */

function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = typeof Ctor == 'function' && Ctor.prototype || objectProto$3;
  return value === proto;
}

var _isPrototype = isPrototype;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function (arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeKeys = _overArg(Object.keys, Object);
var _nativeKeys = nativeKeys;

/** Used for built-in method references. */

var objectProto$2 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$2 = objectProto$2.hasOwnProperty;
/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */

function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }

  var result = [];

  for (var key in Object(object)) {
    if (hasOwnProperty$2.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }

  return result;
}

var _baseKeys = baseKeys;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */

function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */

function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}

var keys_1 = keys;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */

function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}

var _getAllKeys = getAllKeys;

/** Used to compose bitmasks for value comparisons. */

var COMPARE_PARTIAL_FLAG$1 = 1;
/** Used for built-in method references. */

var objectProto$1 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$1 = objectProto$1.hasOwnProperty;
/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$1,
      objProps = _getAllKeys(object),
      objLength = objProps.length,
      othProps = _getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }

  var index = objLength;

  while (index--) {
    var key = objProps[index];

    if (!(isPartial ? key in other : hasOwnProperty$1.call(other, key))) {
      return false;
    }
  } // Check that cyclic values are equal.


  var objStacked = stack.get(object);
  var othStacked = stack.get(other);

  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }

  var result = true;
  stack.set(object, other);
  stack.set(other, object);
  var skipCtor = isPartial;

  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial ? customizer(othValue, objValue, key, other, object, stack) : customizer(objValue, othValue, key, object, other, stack);
    } // Recursively compare objects (susceptible to call stack limits).


    if (!(compared === undefined ? objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack) : compared)) {
      result = false;
      break;
    }

    skipCtor || (skipCtor = key == 'constructor');
  }

  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor; // Non `Object` object instances with different constructors are not equal.

    if (objCtor != othCtor && 'constructor' in object && 'constructor' in other && !(typeof objCtor == 'function' && objCtor instanceof objCtor && typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }

  stack['delete'](object);
  stack['delete'](other);
  return result;
}

var _equalObjects = equalObjects;

/* Built-in method references that are verified to be native. */

var DataView = _getNative(_root, 'DataView');
var _DataView = DataView;

/* Built-in method references that are verified to be native. */

var Promise$1 = _getNative(_root, 'Promise');
var _Promise = Promise$1;

/* Built-in method references that are verified to be native. */

var Set$1 = _getNative(_root, 'Set');
var _Set = Set$1;

/* Built-in method references that are verified to be native. */

var WeakMap = _getNative(_root, 'WeakMap');
var _WeakMap = WeakMap;

/** `Object#toString` result references. */

var mapTag = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag = '[object Set]',
    weakMapTag = '[object WeakMap]';
var dataViewTag = '[object DataView]';
/** Used to detect maps, sets, and weakmaps. */

var dataViewCtorString = _toSource(_DataView),
    mapCtorString = _toSource(_Map),
    promiseCtorString = _toSource(_Promise),
    setCtorString = _toSource(_Set),
    weakMapCtorString = _toSource(_WeakMap);
/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */

var getTag = _baseGetTag; // Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.

if (_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag || _Map && getTag(new _Map()) != mapTag || _Promise && getTag(_Promise.resolve()) != promiseTag || _Set && getTag(new _Set()) != setTag || _WeakMap && getTag(new _WeakMap()) != weakMapTag) {
  getTag = function (value) {
    var result = _baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? _toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString:
          return dataViewTag;

        case mapCtorString:
          return mapTag;

        case promiseCtorString:
          return promiseTag;

        case setCtorString:
          return setTag;

        case weakMapCtorString:
          return weakMapTag;
      }
    }

    return result;
  };
}

var _getTag = getTag;

/** Used to compose bitmasks for value comparisons. */

var COMPARE_PARTIAL_FLAG = 1;
/** `Object#toString` result references. */

var argsTag = '[object Arguments]',
    arrayTag = '[object Array]',
    objectTag = '[object Object]';
/** Used for built-in method references. */

var objectProto = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty = objectProto.hasOwnProperty;
/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray_1(object),
      othIsArr = isArray_1(other),
      objTag = objIsArr ? arrayTag : _getTag(object),
      othTag = othIsArr ? arrayTag : _getTag(other);
  objTag = objTag == argsTag ? objectTag : objTag;
  othTag = othTag == argsTag ? objectTag : othTag;
  var objIsObj = objTag == objectTag,
      othIsObj = othTag == objectTag,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer_1(object)) {
    if (!isBuffer_1(other)) {
      return false;
    }

    objIsArr = true;
    objIsObj = false;
  }

  if (isSameTag && !objIsObj) {
    stack || (stack = new _Stack());
    return objIsArr || isTypedArray_1(object) ? _equalArrays(object, other, bitmask, customizer, equalFunc, stack) : _equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }

  if (!(bitmask & COMPARE_PARTIAL_FLAG)) {
    var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;
      stack || (stack = new _Stack());
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }

  if (!isSameTag) {
    return false;
  }

  stack || (stack = new _Stack());
  return _equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

var _baseIsEqualDeep = baseIsEqualDeep;

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */

function baseIsEqual(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }

  if (value == null || other == null || !isObjectLike_1(value) && !isObjectLike_1(other)) {
    return value !== value && other !== other;
  }

  return _baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
}

var _baseIsEqual = baseIsEqual;

/**
 * Performs a deep comparison between two values to determine if they are
 * equivalent.
 *
 * **Note:** This method supports comparing arrays, array buffers, booleans,
 * date objects, error objects, maps, numbers, `Object` objects, regexes,
 * sets, strings, symbols, and typed arrays. `Object` objects are compared
 * by their own, not inherited, enumerable properties. Functions and DOM
 * nodes are compared by strict equality, i.e. `===`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.isEqual(object, other);
 * // => true
 *
 * object === other;
 * // => false
 */

function isEqual(value, other) {
  return _baseIsEqual(value, other);
}

var isEqual_1 = isEqual;

const WorkshopContext = /*#__PURE__*/createContext(null);

function useWorkshop() {
  const workshop = useContext(WorkshopContext);

  if (!workshop) {
    throw new Error('Workshop: missing context value');
  }

  return workshop;
}

const btoa = typeof window === 'undefined' ? str => Buffer.from(str, 'binary').toString('base64') : window.btoa;
const atob = typeof window === 'undefined' ? str => Buffer.from(str, 'base64').toString('binary') : window.atob;
const uint8ArrayToBase64 = uint8array => {
  let str = '';

  for (let i = 0, {
    length
  } = uint8array; i < length; i++) {
    str += String.fromCharCode(uint8array[i]);
  }

  return btoa(str);
};

function base64ToUint8Array(base64) {
  const binStr = atob(base64);
  const len = binStr.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binStr.charCodeAt(i);
  }

  return bytes;
}

function decode(input) {
  if (input.length === 0) return '';
  const arr = base64ToUint8Array(input);
  return pako.inflate(arr, {
    to: 'string'
  });
}
function encode(input) {
  if (input.length === 0) return '';
  const arr = pako.deflate(input);
  return uint8ArrayToBase64(arr);
}

function encodeValue(val) {
  return encode(JSON.stringify(val));
}
function decodeValue(val) {
  try {
    return JSON.parse(decode(val));
  } catch (_) {
    return {};
  }
}

function propsReducer(state, msg) {
  if (msg.type === 'workshop/props/setValue') {
    return _extends({}, state, {
      value: msg.value
    });
  }

  if (msg.type === 'workshop/props/registerProp') {
    return _extends({}, state, {
      schemas: state.schemas.concat([msg.schema])
    });
  }

  if (msg.type === 'workshop/props/unregisterProp') {
    const value = _extends({}, state.value);

    delete value[msg.name];
    return _extends({}, state, {
      schemas: state.schemas.filter(s => s.name !== msg.name),
      value
    });
  }

  if (msg.type === 'workshop/props/setPropValue') {
    return _extends({}, state, {
      value: _extends({}, state.value, {
        [msg.name]: msg.value
      })
    });
  }

  return state;
}

const PropsProvider = /*#__PURE__*/memo(function PropsProvider(props) {
  const {
    children
  } = props;
  const {
    channel,
    broadcast,
    payload
  } = useWorkshop();
  const encodedValue = payload.value;
  const encodedValueRef = useRef(encodedValue);
  const [{
    schemas,
    value
  }, setState] = useState({
    schemas: [],
    value: decodeValue(String(encodedValue)) || {}
  });
  const registerProp = useCallback(schema => {
    broadcast({
      type: 'workshop/props/registerProp',
      schema
    });
  }, [broadcast]);
  const unregisterProp = useCallback(name => {
    broadcast({
      type: 'workshop/props/unregisterProp',
      name
    });
  }, [broadcast]);
  const setPropValue = useCallback((name, value) => {
    broadcast({
      type: 'workshop/props/setPropValue',
      name,
      value
    });
  }, [broadcast]);
  const ctx = useMemo(() => ({
    registerProp,
    schemas,
    setPropValue,
    unregisterProp,
    value
  }), [registerProp, schemas, setPropValue, unregisterProp, value]); // Subscribe to global messages

  useEffect(() => channel.subscribe(msg => {
    setState(prevState => {
      const nextState = propsReducer(prevState, msg);

      if (isEqual_1(prevState, nextState)) {
        return prevState;
      }

      return nextState;
    });
  }), [channel]);
  useEffect(() => {
    const nextEncodedValue = encodeValue(value);

    if (encodedValueRef.current !== nextEncodedValue) {
      encodedValueRef.current = nextEncodedValue;
      broadcast({
        type: 'workshop/setPayloadValue',
        key: 'value',
        value: nextEncodedValue
      });
    }
  }, [broadcast, value]);
  useEffect(() => {
    if (encodedValueRef.current === encodedValue) {
      return;
    }

    encodedValueRef.current = encodedValue;
    setState(prevState => _extends({}, prevState, {
      value: decodeValue(String(encodedValue)) || {}
    }));
  }, [encodedValue]);
  return /*#__PURE__*/React.createElement(PropsContext.Provider, {
    value: ctx
  }, children);
});

function useBoolean(name, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    unregisterProp,
    value
  } = useProps();
  useEffect(() => {
    registerProp({
      type: 'boolean',
      groupName,
      name,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, registerProp, unregisterProp]);
  return value[name] === undefined ? defaultValue : value[name];
}

function useNumber(name, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    unregisterProp,
    value
  } = useProps();
  useEffect(() => {
    registerProp({
      type: 'number',
      groupName,
      name,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, registerProp, unregisterProp]);
  return value[name] === undefined ? defaultValue : value[name];
}

function useSelect(name, options, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    unregisterProp,
    value
  } = useProps();
  useEffect(() => {
    registerProp({
      type: 'select',
      groupName,
      name,
      options: options,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, options, registerProp, unregisterProp]);
  return value[name] === undefined ? defaultValue : value[name];
}

function useString(name, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    unregisterProp,
    value
  } = useProps();
  useEffect(() => {
    registerProp({
      type: 'string',
      groupName,
      name,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, registerProp, unregisterProp]);
  return value[name] === undefined ? defaultValue : value[name];
}

function useText(name, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    unregisterProp,
    value
  } = useProps();
  useEffect(() => {
    registerProp({
      type: 'text',
      groupName,
      name,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, registerProp, unregisterProp]);
  return value[name] === undefined ? defaultValue : value[name];
}

function propsPlugin() {
  return {
    name: 'props',
    title: 'Properties',
    inspector: PropsInspector,
    provider: PropsProvider
  };
}

const WorkshopProvider = /*#__PURE__*/memo(function WorkshopProvider(props) {
  const {
    broadcast,
    children,
    channel,
    config,
    frameReady,
    origin,
    path,
    payload,
    scheme,
    viewport = 'auto',
    zoom = 1
  } = props;
  const {
    plugins: pluginsProp = EMPTY_ARRAY,
    collections = EMPTY_ARRAY,
    frameUrl = '/frame/',
    scopes,
    title = 'Workshop'
  } = config;

  if (!payload) {
    throw new Error('missing `payload` property');
  }

  const plugins = useMemo(() => [propsPlugin(), ...pluginsProp], [pluginsProp]);
  const {
    scope,
    story
  } = useMemo(() => resolveLocation(scopes, path), [path, scopes]);
  const workshop = useMemo(() => ({
    plugins,
    broadcast,
    channel,
    collections,
    frameReady,
    frameUrl,
    origin,
    path,
    payload,
    scheme,
    scope,
    scopes,
    story,
    title,
    viewport,
    zoom
  }), [plugins, broadcast, channel, collections, frameReady, frameUrl, origin, path, payload, scheme, scope, scopes, story, title, viewport, zoom]);
  let wrappedChildren = children;

  for (const plugin of plugins) {
    if (plugin.provider) {
      wrappedChildren = /*#__PURE__*/createElement(plugin.provider, {}, wrappedChildren);
    }
  }

  return /*#__PURE__*/React.createElement(WorkshopContext.Provider, {
    value: workshop
  }, wrappedChildren);
});

function workshopReducer(state, msg) {
  if (msg.type === 'workshop/frameReady') {
    return _extends({}, state, {
      frameReady: true
    });
  }

  if (msg.type === 'workshop/setState') {
    if (isEqual_1(state, msg.value)) {
      return state;
    }

    return msg.value;
  }

  if (msg.type === 'workshop/setZoom') {
    if (state.zoom === msg.value) return state;
    return _extends({}, state, {
      zoom: msg.value
    });
  }

  if (msg.type === 'workshop/setViewport') {
    if (state.viewport === msg.value) return state;
    return _extends({}, state, {
      viewport: msg.value
    });
  }

  if (msg.type === 'workshop/toggleScheme') {
    return _extends({}, state, {
      scheme: state.scheme === 'light' ? 'dark' : 'light'
    });
  }

  if (msg.type === 'workshop/setScheme') {
    if (state.scheme === msg.value) return state;
    return _extends({}, state, {
      scheme: msg.value
    });
  }

  if (msg.type === 'workshop/setPath') {
    if (state.path === msg.value) return state;
    return _extends({}, state, {
      path: msg.value
    });
  }

  if (msg.type === 'workshop/setPayload') {
    if (isEqual_1(state.payload, msg.value)) {
      return state;
    }

    return _extends({}, state, {
      payload: msg.value
    });
  }

  if (msg.type === 'workshop/setPayloadValue') {
    const payload = _extends({}, state.payload, {
      [msg.key]: msg.value
    });

    if (isEqual_1(state.payload, payload)) {
      return state;
    }

    return _extends({}, state, {
      payload
    });
  }

  return state;
}

const ROOT_PATH = (() => {
  // Wrap in try/catch to avoid throwing exception in environments that don’t have `process.env`.
  try {
    return process.env.ROOT_PATH;
  } catch (_) {
    return undefined;
  }
})();

const RE_URL = /http:\/\/([^:/\s]+)(:[0-9]+)?/g;
const RE_VITE_FS_PREFIX = /\/@fs\//g;
const RE_VITE_FS_SUFFIX = /\?([a-z]{1})=([0-9]+)/g;
function formatStack(stack) {
  let ret = decodeURIComponent(stack);
  ret = stack.replace(RE_URL, '').replace(RE_VITE_FS_PREFIX, '/').replace(RE_VITE_FS_SUFFIX, '');
  if (ROOT_PATH) return replaceRootPath(ret, ROOT_PATH + '/');
  return ret;
}

function replaceRootPath(str, rootPath) {
  const re = new RegExp(rootPath.replace(/\//g, '\\/'), 'g');
  return str.replace(re, '');
}

const WorkshopCanvas$1 = /*#__PURE__*/memo(function WorkshopCanvas() {
  const {
    story
  } = useWorkshop();
  const [state, setState] = useState({
    error: null,
    errorInfo: null
  });
  const catchError = useCallback(({
    error,
    info: errorInfo
  }) => {
    setState({
      error,
      errorInfo
    });
  }, []);
  const handleRetry = useCallback(() => {
    setState({
      error: null,
      errorInfo: null
    });
  }, []);

  if (!story) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }

  if (state.error) {
    return /*#__PURE__*/React.createElement(Card, {
      as: "main",
      height: "fill",
      overflow: "auto",
      tone: "critical"
    }, /*#__PURE__*/React.createElement(ErrorScreen, {
      error: state.error,
      errorInfo: state.errorInfo,
      onRetry: handleRetry
    }));
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h1", {
    hidden: true
  }, story.title), /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(LoadingScreen, {
      story: story
    })
  }, /*#__PURE__*/React.createElement(Card, {
    as: "main",
    height: "fill"
  }, /*#__PURE__*/React.createElement(ErrorBoundary, {
    onCatch: catchError
  }, /*#__PURE__*/createElement(story.component)))));
});
const LoadingScreen = /*#__PURE__*/memo(function LoadingScreen(props) {
  const {
    story
  } = props;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h1", {
    hidden: true
  }, "Loading ", /*#__PURE__*/React.createElement("em", null, story.title), "\u2026"), /*#__PURE__*/React.createElement(Flex, {
    align: "center",
    as: "main",
    height: "fill",
    justify: "center"
  }, /*#__PURE__*/React.createElement(Spinner, {
    muted: true
  })));
});
const ErrorScreen = /*#__PURE__*/memo(function ErrorScreen(props) {
  const {
    error,
    errorInfo,
    onRetry
  } = props;
  return /*#__PURE__*/React.createElement(Box, {
    padding: 4
  }, /*#__PURE__*/React.createElement(Stack$1, {
    space: 4
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h1",
    size: [1, 1, 2]
  }, error.message), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Button, {
    onClick: onRetry,
    text: "Retry"
  })), error.stack && /*#__PURE__*/React.createElement(Code, {
    size: 1
  }, formatStack(error.stack)), errorInfo && /*#__PURE__*/React.createElement(Code, {
    size: 1
  }, 'Component stack:' + formatStack(errorInfo.componentStack))));
});

function isArray(value) {
  return Array.isArray(value);
}

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function createMainController() {
  const _subscribers = new Set();

  let _msgQueue = [];
  let _flushTimeout = null;

  function _flush() {
    if (_flushTimeout) {
      clearInterval(_flushTimeout);
    }

    _flushTimeout = setTimeout(() => {
      window.parent.postMessage(_msgQueue);
      _msgQueue = [];
      _flushTimeout = null;
    }, 0);
  }

  function _handleMessage(event) {
    const msgs = event.data;

    if (isArray(msgs)) {
      for (const msg of msgs) {
        if (isRecord(msg) && typeof msg.type === 'string' && msg.type.startsWith('workshop/')) {
          for (const subscriber of _subscribers) {
            subscriber(msg);
          }
        }
      }
    }
  }

  function _mount() {
    window.addEventListener('message', _handleMessage, false);
  }

  function _unmount() {
    window.removeEventListener('message', _handleMessage, false);
  }

  return {
    message: {
      publish(msg) {
        _msgQueue.push(msg);

        _flush();
      },

      subscribe(subscriber) {
        _subscribers.add(subscriber);

        if (_subscribers.size === 1) {
          _mount();
        }

        return () => {
          _subscribers.delete(subscriber);

          if (_subscribers.size === 0) {
            _unmount();
          }
        };
      }

    }
  };
}

const _excluded$1 = ["path", "scheme", "viewport", "zoom"];

function getStateFromLocation$1() {
  const query = typeof window === 'undefined' ? {} : qs.parse(window.location.search.slice(1));

  const {
    path = '/',
    scheme,
    viewport,
    zoom
  } = query,
        payload = _objectWithoutPropertiesLoose(query, _excluded$1);

  return {
    frameReady: false,
    path,
    payload,
    scheme: typeof scheme === 'string' ? scheme : 'light',
    viewport: typeof viewport === 'string' ? viewport : 'auto',
    zoom: typeof zoom === 'string' ? Number(zoom) : 1
  };
}

const WorkshopFrame = /*#__PURE__*/memo(function WorkshopFrame(props) {
  const {
    config,
    setScheme
  } = props;
  const main = useMemo(() => createMainController(), []);
  const channel = useMemo(() => createPubsub(), []);
  const [boundaryElement, setBoundaryElement] = useState(null);
  const [portalElement, setPortalElement] = useState(null); // Publish messages to both frame+main

  const broadcast = useCallback(msg => {
    // Handle msg
    channel.publish(msg); // Pass message to main

    main.message.publish(msg);
  }, [channel, main]);
  const [{
    frameReady,
    path,
    payload,
    scheme,
    viewport,
    zoom
  }, setState] = useState(() => getStateFromLocation$1()); // Subscribe to global messages

  useEffect(() => channel.subscribe(msg => setState(s => workshopReducer(s, msg))), [channel]); // Pipe messages from main to channel

  useEffect(() => main.message.subscribe(channel.publish), [channel, main]); // Update scheme

  useEffect(() => setScheme(scheme), [setScheme, scheme]); // Inform `main` that the frame is ready

  useEffect(() => broadcast({
    type: 'workshop/frameReady'
  }), [broadcast]);
  return /*#__PURE__*/React.createElement(ToastProvider, null, /*#__PURE__*/React.createElement(BoundaryElementProvider, {
    element: boundaryElement
  }, /*#__PURE__*/React.createElement(PortalProvider, {
    element: portalElement
  }, /*#__PURE__*/React.createElement(WorkshopProvider, {
    broadcast: broadcast,
    config: config,
    channel: channel,
    frameReady: frameReady,
    origin: "frame",
    path: path,
    payload: payload,
    scheme: scheme,
    viewport: viewport,
    zoom: zoom
  }, /*#__PURE__*/React.createElement(Card, {
    height: "fill",
    tone: "transparent",
    ref: setBoundaryElement
  }, /*#__PURE__*/React.createElement(WorkshopCanvas$1, null), /*#__PURE__*/React.createElement("div", {
    "data-portal": "",
    ref: setPortalElement
  }))))));
});

function _buildLocationUrl(loc) {
  const search = qs.stringify(loc.query || {});
  return `${loc.path}${search ? `?${search}` : ''}`;
}

function _getStateFromWindow() {
  return {
    path: location.pathname,
    query: qs.parse(location.search.substr(1))
  };
}

function createLocationStore() {
  const _subscribers = new Set();

  function _handlePopState() {
    _notifySubscribers(_extends({
      type: 'pop'
    }, _getStateFromWindow()));
  }

  function _notifySubscribers(loc) {
    for (const subscriber of _subscribers) {
      subscriber(loc);
    }
  }

  function _mount() {
    window.addEventListener('popstate', _handlePopState);
  }

  function _unmount() {
    window.removeEventListener('popstate', _handlePopState);
  }

  return {
    get() {
      return _getStateFromWindow();
    },

    push(nextLocation) {
      window.history.pushState(null, document.title, _buildLocationUrl(nextLocation));

      _notifySubscribers(_extends({
        type: 'push'
      }, nextLocation));
    },

    replace(nextLocation) {
      window.history.replaceState(null, document.title, _buildLocationUrl(nextLocation));

      _notifySubscribers(_extends({
        type: 'replace'
      }, nextLocation));
    },

    subscribe(subscribe) {
      _subscribers.add(subscribe);

      if (_subscribers.size === 1) _mount();
      return () => {
        _subscribers.delete(subscribe);

        if (_subscribers.size === 0) _unmount();
      };
    }

  };
}

const A11yContext = /*#__PURE__*/createContext(null);

function useA11y() {
  const props = useContext(A11yContext);

  if (!props) {
    throw new Error('A11y: missing context value');
  }

  return props;
}

const A11yInspector = /*#__PURE__*/memo(function A11yInspector() {
  const {
    results
  } = useA11y();
  return /*#__PURE__*/React.createElement(Box, {
    padding: 2
  }, results && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Stack$1, {
    space: 2
  }, results.violations.map(violation => /*#__PURE__*/React.createElement(ResultItem, {
    key: violation.id,
    result: violation,
    tone: "critical"
  })), results.passes.map(violation => /*#__PURE__*/React.createElement(ResultItem, {
    key: violation.id,
    result: violation,
    tone: "positive"
  })))));
});
const ResultItem = /*#__PURE__*/memo(function ResultItem(props) {
  const {
    result,
    tone
  } = props;
  return /*#__PURE__*/React.createElement(Card, {
    border: true,
    radius: 2,
    tone: tone
  }, /*#__PURE__*/React.createElement(Stack$1, {
    paddingX: 2,
    paddingY: 3,
    space: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, result.help, result.impact && /*#__PURE__*/React.createElement(React.Fragment, null, " (", result.impact, ")")), /*#__PURE__*/React.createElement(Text, {
    muted: true,
    size: 1
  }, result.description), /*#__PURE__*/React.createElement(Text, {
    size: 1
  }, /*#__PURE__*/React.createElement("a", {
    href: result.helpUrl,
    target: "_blank",
    rel: "noreferrer"
  }, "Help URL \u2192"))), /*#__PURE__*/React.createElement(Card, {
    borderTop: true,
    tone: "inherit"
  }), /*#__PURE__*/React.createElement(Stack$1, {
    padding: 2,
    space: 2
  }, result.nodes.map((node, nodeIndex) => {
    return /*#__PURE__*/React.createElement(Stack$1, {
      key: nodeIndex,
      space: 2
    }, node.failureSummary && /*#__PURE__*/React.createElement(Text, {
      size: 1
    }, node.failureSummary), /*#__PURE__*/React.createElement(Card, {
      border: true,
      overflow: "auto",
      padding: 2,
      radius: 2,
      tone: "inherit"
    }, /*#__PURE__*/React.createElement(Code, {
      language: "html",
      size: 0
    }, node.html)));
  })));
});

const A11yProvider = /*#__PURE__*/memo(function A11yProvider(props) {
  const {
    children
  } = props;
  const {
    broadcast,
    channel,
    origin,
    path
  } = useWorkshop();
  const [{
    error,
    results
  }, setState] = useState({
    error: null,
    results: null
  });
  const a11y = useMemo(() => ({
    error,
    results
  }), [error, results]);
  useEffect(() => {
    if (origin === 'main') return;
    axe.run().then(results => {
      broadcast({
        type: 'workshop/a11y/setResults',
        path,
        results
      });
    }).catch(error => {
      broadcast({
        type: 'workshop/a11y/setError',
        message: error.message
      });
    });
  }, [broadcast, origin, path]);
  useEffect(() => {
    channel.subscribe(msg => {
      if (msg.type === 'workshop/setPath') {
        setState(s => _extends({}, s, {
          error: null,
          results: null
        }));
      }

      if (msg.type === 'workshop/a11y/setResults') {
        setState(s => _extends({}, s, {
          error: null,
          results: msg.results
        }));
      }

      if (msg.type === 'workshop/a11y/setError') {
        setState(s => _extends({}, s, {
          error: {
            message: msg.message
          },
          results: null
        }));
      }
    });
  }, [channel]);
  return /*#__PURE__*/React.createElement(A11yContext.Provider, {
    value: a11y
  }, children);
});

function a11yPlugin() {
  return {
    name: 'a11y',
    title: 'A11y',
    inspector: A11yInspector,
    provider: A11yProvider
  };
}

const PerfContext = /*#__PURE__*/createContext(null);

function usePerf() {
  const perf = useContext(PerfContext);

  if (!perf) {
    throw new Error('Perf: missing context value');
  }

  return perf;
}

function usePerfTest(props) {
  const {
    name,
    title,
    description,
    run
  } = props;
  const {
    activeTest,
    addRenderResult,
    registerTest
  } = usePerf();
  const active = activeTest === name;
  const ref = useRef(null);
  const test = useMemo(() => ({
    description,
    name,
    ref,
    run,
    title
  }), [description, name, ref, run, title]);
  const handleRender = useCallback((id, phase, actualDuration, baseDuration, startTime, commitTime, interactions) => {
    const result = {
      id,
      phase,
      actualDuration,
      baseDuration,
      startTime,
      commitTime,
      interactions
    };
    setTimeout(() => {
      // eslint-disable-next-line
      console.log('@todo: render', {
        addRenderResult,
        name,
        result
      }); // @todo: this causes an infinite render-loop – why???
      // addRenderResult(name, result)
    }, 0);
  }, [addRenderResult, name]);
  const Wrapper = useMemo(() => {
    return /*#__PURE__*/memo(function Wrapper({
      children
    }) {
      if (!active) {
        return /*#__PURE__*/React.createElement(React.Fragment, null, children);
      }

      return /*#__PURE__*/React.createElement(Profiler, {
        id: name,
        onRender: handleRender
      }, children);
    });
  }, [active, handleRender, name]);
  useEffect(() => registerTest(test), [registerTest, test]);
  return useMemo(() => ({
    ref,
    Wrapper
  }), [ref, Wrapper]);
}

const PerfInspector = /*#__PURE__*/memo(function PerfInspector() {
  const {
    clearResults,
    results,
    runTest,
    testDetails
  } = usePerf();

  if (testDetails.length === 0) {
    return /*#__PURE__*/React.createElement(Box, {
      padding: 2
    }, /*#__PURE__*/React.createElement(Box, {
      padding: 2
    }, /*#__PURE__*/React.createElement(Text, {
      muted: true,
      size: 1
    }, "No tests")));
  }

  return /*#__PURE__*/React.createElement(Stack$1, {
    padding: 2,
    space: 2
  }, testDetails.map(detail => {
    const testResults = results.filter(r => r.name === detail.name);
    return /*#__PURE__*/React.createElement(Card, {
      border: true,
      key: detail.name,
      overflow: "hidden",
      radius: 2,
      style: {
        lineHeight: 0
      }
    }, /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(Stack$1, {
      flex: 1,
      padding: 2,
      space: 2
    }, /*#__PURE__*/React.createElement(Text, {
      size: 1,
      weight: "semibold"
    }, detail.title || detail.name), detail.description && /*#__PURE__*/React.createElement(Text, {
      muted: true,
      size: 1
    }, detail.description)), /*#__PURE__*/React.createElement(Flex, {
      align: "flex-start",
      gap: 1,
      padding: 1
    }, /*#__PURE__*/React.createElement(Button, {
      "aria-label": "Clear results",
      fontSize: 1,
      icon: TrashIcon,
      mode: "bleed",
      onClick: () => clearResults(detail.name),
      padding: 1
    }))), /*#__PURE__*/React.createElement(Stack$1, null, testResults.map((result, resultIndex) => {
      var _result$timing, _result$timing2;

      return /*#__PURE__*/React.createElement(Card, {
        borderTop: true,
        key: resultIndex
      }, /*#__PURE__*/React.createElement(Stack$1, null, result.renders.map((r, rIdx) => {
        return /*#__PURE__*/React.createElement(Card, {
          key: rIdx
        }, r.phase);
      })), /*#__PURE__*/React.createElement(Flex, {
        padding: 2
      }, /*#__PURE__*/React.createElement(Box, {
        flex: 1
      }, /*#__PURE__*/React.createElement(Text, {
        muted: true,
        size: 1
      }, (_result$timing = result.timing) == null ? void 0 : _result$timing.runs, " runs")), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
        size: 1
      }, "Avg. ", (_result$timing2 = result.timing) == null ? void 0 : _result$timing2.avgDuration.toFixed(3), "ms"))));
    }), /*#__PURE__*/React.createElement(Card, {
      borderTop: true
    }, /*#__PURE__*/React.createElement(Stack$1, null, /*#__PURE__*/React.createElement(Button, {
      "aria-label": "Run",
      fontSize: 1,
      icon: PlayIcon,
      mode: "bleed",
      onClick: () => runTest(detail.name),
      padding: 2,
      radius: 0
    })))));
  }));
});

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function _runTest(test, target) {
  // // Do a run to heat up the browser
  // await test.run({target})
  const runs = 1;
  performance.clearMarks();
  performance.clearMeasures();

  for (let i = 0; i < runs; i += 1) {
    performance.mark(`start:${test.name}`);
    await test.run({
      target
    });
    performance.mark(`end:${test.name}`);
    performance.measure(test.name, `start:${test.name}`, `end:${test.name}`); // sleep

    await delay(100);
  }

  const perfEntries = performance.getEntriesByName(test.name);
  const sumDuration = perfEntries.reduce((t, e) => t + e.duration, 0);
  const avgDuration = sumDuration / runs;
  return {
    avgDuration,
    sumDuration,
    runs
  };
}

function perfReducer(state, msg) {
  if (msg.type === 'workshop/perf/clearResults') {
    return _extends({}, state, {
      results: state.results.filter(d => d.name !== msg.name)
    });
  }

  if (msg.type === 'workshop/perf/registerTest') {
    return _extends({}, state, {
      testDetails: state.testDetails.concat([{
        description: msg.description,
        name: msg.name,
        title: msg.title
      }])
    });
  }

  if (msg.type === 'workshop/perf/unregisterTest') {
    return _extends({}, state, {
      testDetails: state.testDetails.filter(d => d.name !== msg.name)
    });
  }

  if (msg.type === 'workshop/perf/runTest') {
    return _extends({}, state, {
      activeTest: msg.name,
      results: state.results.concat([{
        name: msg.name,
        renders: []
      }])
    });
  }

  if (msg.type === 'workshop/perf/addResult') {
    if (state.activeTest === msg.name) {
      const result = state.results.filter(r => r.name === msg.name);
      const lastResult = result[result.length - 1];

      if (lastResult) {
        return _extends({}, state, {
          results: state.results.map(r => {
            if (r === lastResult) {
              return _extends({}, r, {
                timing: msg.result
              });
            }

            return r;
          })
        });
      }
    }

    return state;
  }

  if (msg.type === 'workshop/perf/addRenderResult') {
    if (state.activeTest === msg.name) {
      const result = state.results.filter(r => r.name === msg.name);
      const lastResult = result[result.length - 1];

      if (lastResult) {
        return _extends({}, state, {
          results: state.results.map(r => {
            if (r === lastResult) {
              return _extends({}, r, {
                renders: r.renders.concat([msg.result])
              });
            }

            return r;
          })
        });
      }
    }

    return state;
  }

  return state;
}

const PerfProvider = /*#__PURE__*/memo(function PerfProvider(props) {
  const {
    children
  } = props;
  const {
    broadcast,
    channel
  } = useWorkshop();
  const [{
    activeTest,
    results,
    testDetails,
    tests
  }, setState] = useState({
    results: [],
    testDetails: [],
    tests: []
  });
  const addRenderResult = useCallback((name, result) => {
    broadcast({
      type: 'workshop/perf/addRenderResult',
      name,
      result
    });
  }, [broadcast]);
  const clearResults = useCallback(name => {
    broadcast({
      type: 'workshop/perf/clearResults',
      name
    });
  }, [broadcast]);
  const registerTest = useCallback(test => {
    broadcast({
      type: 'workshop/perf/registerTest',
      description: test.description,
      title: test.title,
      name: test.name
    });
    setState(s => _extends({}, s, {
      tests: s.tests.concat([test])
    }));
    return () => {
      broadcast({
        type: 'workshop/perf/unregisterTest',
        name: test.name
      });
      setState(s => _extends({}, s, {
        tests: s.tests.filter(t => t !== test)
      }));
    };
  }, [broadcast]);
  const runTest = useCallback(testName => {
    broadcast({
      type: 'workshop/perf/runTest',
      name: testName
    });
  }, [broadcast]);
  const perf = useMemo(() => ({
    activeTest,
    addRenderResult,
    clearResults,
    registerTest,
    results,
    runTest,
    testDetails,
    tests
  }), [activeTest, addRenderResult, clearResults, registerTest, results, runTest, testDetails, tests]);
  useEffect(() => {
    return channel.subscribe(msg => {
      setState(s => perfReducer(s, msg));

      if (msg.type === 'workshop/perf/runTest') {
        setTimeout(() => {
          const test = tests.find(t => t.name === msg.name);

          if (test) {
            const container = test.ref.current;

            if (container !== null) {
              _runTest(test, container).then(result => {
                broadcast({
                  type: 'workshop/perf/addResult',
                  name: test.name,
                  result
                });
              }).catch(err => {
                // eslint-disable-next-line no-console
                console.error(err);
              });
            }
          }
        }, 0);
        return;
      }
    });
  }, [broadcast, channel, tests]);
  return /*#__PURE__*/React.createElement(PerfContext.Provider, {
    value: perf
  }, children);
});

function perfPlugin() {
  return {
    name: 'perf',
    title: 'Performance',
    inspector: PerfInspector,
    provider: PerfProvider
  };
}

const MemoTab = /*#__PURE__*/memo(Tab);
const WorkshopInspector = /*#__PURE__*/memo(function WorkshopInspector() {
  const {
    plugins
  } = useWorkshop();
  const tabs = useMemo(() => {
    return plugins.filter(plugin => plugin.inspector).map(plugin => {
      return {
        id: plugin.name,
        label: plugin.title,
        tone: undefined,
        panel: plugin.inspector
      };
    });
  }, [plugins]);
  const [tabId, setTabId] = useState(tabs.length > 0 ? tabs[0].id : null);
  const currentTab = tabs.find(tab => tab.id === tabId);
  const showTabs = tabs.length > 1;
  return /*#__PURE__*/React.createElement(Card, {
    borderLeft: true,
    display: ['none', 'none', 'block'],
    flex: 1,
    overflow: "auto",
    style: {
      minWidth: 180,
      maxWidth: 300
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    height: "fill"
  }, showTabs && /*#__PURE__*/React.createElement(InspectorHeaderWithTabs, {
    currentTabId: tabId,
    onTabChange: setTabId,
    tabs: tabs
  }), showTabs && tabs.map(tab => /*#__PURE__*/React.createElement(TabPanel, {
    "aria-labelledby": `${tab.id}-tab`,
    flex: 1,
    hidden: tab.id !== tabId,
    id: `${tab.id}-panel`,
    key: tab.id,
    overflow: "auto"
  }, tab.panel && /*#__PURE__*/createElement(tab.panel))), !showTabs && (currentTab == null ? void 0 : currentTab.panel) && /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    overflow: "auto"
  }, /*#__PURE__*/React.createElement(MemoRender, {
    component: currentTab.panel
  }))));
});
const InspectorHeaderWithTabs = /*#__PURE__*/memo(function InspectorHeaderWithTabs(props) {
  const {
    currentTabId,
    onTabChange,
    tabs
  } = props;
  return /*#__PURE__*/React.createElement(Layer, {
    style: {
      flex: 'none',
      position: 'sticky',
      top: 0
    }
  }, /*#__PURE__*/React.createElement(Card, {
    padding: 2,
    shadow: 1,
    style: {
      lineHeight: 0
    }
  }, /*#__PURE__*/React.createElement(TabList, {
    space: 1
  }, tabs.map(tab => /*#__PURE__*/React.createElement(InspectorTabView, {
    key: tab.id,
    onTabChange: onTabChange,
    selected: tab.id === currentTabId,
    tab: tab
  })))));
});

function InspectorTabView(props) {
  const {
    onTabChange,
    selected,
    tab
  } = props;
  const handleClick = useCallback(() => {
    onTabChange(tab.id);
  }, [onTabChange, tab]);
  return /*#__PURE__*/React.createElement(MemoTab, {
    "aria-controls": `${tab.id}-panel`,
    fontSize: 1,
    id: tab.id,
    label: tab.label,
    onClick: handleClick,
    selected: selected,
    tone: tab.tone
  });
}

const MemoRender = /*#__PURE__*/memo(function MemoRender(props) {
  return /*#__PURE__*/createElement(props.component);
});

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */

var now = function now() {
  return _root.Date.now();
};

var now_1 = now;

/** Used to match a single whitespace character. */
var reWhitespace = /\s/;
/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */

function trimmedEndIndex(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}

  return index;
}

var _trimmedEndIndex = trimmedEndIndex;

/** Used to match leading whitespace. */

var reTrimStart = /^\s+/;
/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */

function baseTrim(string) {
  return string ? string.slice(0, _trimmedEndIndex(string) + 1).replace(reTrimStart, '') : string;
}

var _baseTrim = baseTrim;

/** `Object#toString` result references. */

var symbolTag = '[object Symbol]';
/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */

function isSymbol(value) {
  return typeof value == 'symbol' || isObjectLike_1(value) && _baseGetTag(value) == symbolTag;
}

var isSymbol_1 = isSymbol;

/** Used as references for various `Number` constants. */

var NAN = 0 / 0;
/** Used to detect bad signed hexadecimal string values. */

var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;
/** Used to detect binary string values. */

var reIsBinary = /^0b[01]+$/i;
/** Used to detect octal string values. */

var reIsOctal = /^0o[0-7]+$/i;
/** Built-in method references without a dependency on `root`. */

var freeParseInt = parseInt;
/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */

function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }

  if (isSymbol_1(value)) {
    return NAN;
  }

  if (isObject_1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject_1(other) ? other + '' : other;
  }

  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }

  value = _baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return isBinary || reIsOctal.test(value) ? freeParseInt(value.slice(2), isBinary ? 2 : 8) : reIsBadHex.test(value) ? NAN : +value;
}

var toNumber_1 = toNumber;

/** Error message constants. */

var FUNC_ERROR_TEXT = 'Expected a function';
/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeMax = Math.max,
    nativeMin = Math.min;
/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */

function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }

  wait = toNumber_1(wait) || 0;

  if (isObject_1(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber_1(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;
    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time; // Start the timer for the trailing edge.

    timerId = setTimeout(timerExpired, wait); // Invoke the leading edge.

    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;
    return maxing ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime; // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.

    return lastCallTime === undefined || timeSinceLastCall >= wait || timeSinceLastCall < 0 || maxing && timeSinceLastInvoke >= maxWait;
  }

  function timerExpired() {
    var time = now_1();

    if (shouldInvoke(time)) {
      return trailingEdge(time);
    } // Restart the timer.


    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined; // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.

    if (trailing && lastArgs) {
      return invokeFunc(time);
    }

    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }

    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now_1());
  }

  function debounced() {
    var time = now_1(),
        isInvoking = shouldInvoke(time);
    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }

      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }

    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }

    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

var debounce_1 = debounce;

function NavbarBreadcrumbs() {
  const {
    broadcast,
    scope,
    story,
    title
  } = useWorkshop();
  const handleHomeClick = useCallback(event => {
    event.preventDefault();
    broadcast({
      type: 'workshop/setPath',
      value: '/'
    });
  }, [broadcast]);
  return /*#__PURE__*/React.createElement(NavbarBreadcrumbsView, {
    onHomeClick: handleHomeClick,
    scopeTitle: scope == null ? void 0 : scope.title,
    storyTitle: story == null ? void 0 : story.title,
    title: title
  });
}
const NavbarBreadcrumbsView = /*#__PURE__*/memo(function NavbarBreadcrumbsView(props) {
  const {
    onHomeClick,
    scopeTitle,
    storyTitle,
    title
  } = props;
  return /*#__PURE__*/React.createElement(Breadcrumbs, {
    separator: /*#__PURE__*/React.createElement(Text, {
      muted: true,
      size: 1
    }, "/"),
    space: 2
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "bold"
  }, /*#__PURE__*/React.createElement("a", {
    href: "/",
    onClick: onHomeClick,
    style: {
      color: 'inherit'
    }
  }, title)), scopeTitle && /*#__PURE__*/React.createElement(Text, {
    align: "center",
    size: 1
  }, scopeTitle), storyTitle && /*#__PURE__*/React.createElement(Text, {
    size: 1
  }, storyTitle));
});

const OpenCanvasButton = /*#__PURE__*/memo(function OpenCanvasButton() {
  const {
    frameUrl,
    path,
    payload,
    scheme,
    zoom,
    viewport
  } = useWorkshop();
  const canvasUrl = useMemo(() => path === '/' ? undefined : buildFrameUrl({
    baseUrl: frameUrl,
    path,
    payload,
    scheme,
    zoom,
    viewport
  }), [frameUrl, path, payload, scheme, zoom, viewport]);
  return /*#__PURE__*/React.createElement(Button, {
    as: canvasUrl ? 'a' : 'button',
    disabled: !canvasUrl,
    fontSize: 1,
    href: canvasUrl,
    iconRight: LaunchIcon,
    mode: "ghost",
    padding: 2,
    rel: "noopener noreferrer",
    target: "_blank",
    text: "Open story"
  });
});

function SchemeMenu() {
  const {
    broadcast,
    scheme
  } = useWorkshop();
  const handleToggleScheme = useCallback(() => {
    broadcast({
      type: 'workshop/toggleScheme'
    });
  }, [broadcast]);
  return /*#__PURE__*/React.createElement(SchemeMenuView, {
    dark: scheme === 'dark',
    onToggleScheme: handleToggleScheme
  });
}
const SchemeMenuView = /*#__PURE__*/memo(function SchemeMenuView(props) {
  const {
    dark,
    onToggleScheme
  } = props;
  return /*#__PURE__*/React.createElement(Button, {
    fontSize: 1,
    icon: dark ? MoonIcon : SunIcon,
    mode: "bleed",
    onClick: onToggleScheme,
    padding: 2
  });
});

const ViewportMenu = /*#__PURE__*/memo(function ViewportMenu() {
  const {
    broadcast,
    story,
    viewport
  } = useWorkshop();
  const setViewport = useCallback(value => {
    broadcast({
      type: 'workshop/setViewport',
      value
    });
  }, [broadcast]);
  return /*#__PURE__*/React.createElement(ViewportMenuView, {
    disabled: !story,
    setViewport: setViewport,
    viewport: viewport
  });
});
const ViewportMenuView = /*#__PURE__*/memo(function ViewportMenuView(props) {
  var _VIEWPORT_OPTIONS$fin;

  const {
    disabled,
    setViewport,
    viewport
  } = props;
  return /*#__PURE__*/React.createElement(MenuButton, {
    button: /*#__PURE__*/React.createElement(Button, {
      disabled: disabled,
      fontSize: 1,
      iconRight: SelectIcon,
      mode: "ghost",
      padding: 2,
      style: {
        minWidth: 80
      },
      text: (_VIEWPORT_OPTIONS$fin = VIEWPORT_OPTIONS.find(o => o.name === viewport)) == null ? void 0 : _VIEWPORT_OPTIONS$fin.title
    }),
    id: "viewport-menu",
    menu: /*#__PURE__*/React.createElement(Menu, null, VIEWPORT_OPTIONS.map(option => /*#__PURE__*/React.createElement(MenuItem, {
      fontSize: 1,
      key: option.name,
      onClick: () => setViewport(option.name),
      padding: 2,
      selected: option.name === viewport,
      text: option.title
    }))),
    popover: {
      matchReferenceWidth: true
    },
    portal: true
  });
});

function ZoomMenu() {
  const {
    broadcast,
    story,
    zoom
  } = useWorkshop();
  const setZoom = useCallback(value => broadcast({
    type: 'workshop/setZoom',
    value
  }), [broadcast]);
  return /*#__PURE__*/React.createElement(ZoomMenuView, {
    disabled: !story,
    setZoom: setZoom,
    zoom: zoom
  });
}
const ZoomMenuView = /*#__PURE__*/memo(function ZoomMenuView(props) {
  var _ZOOM_OPTIONS$find;

  const {
    disabled,
    setZoom,
    zoom
  } = props;
  return /*#__PURE__*/React.createElement(MenuButton, {
    button: /*#__PURE__*/React.createElement(Button, {
      disabled: disabled,
      fontSize: 1,
      iconRight: SelectIcon,
      mode: "ghost",
      padding: 2,
      style: {
        minWidth: 80
      },
      text: (_ZOOM_OPTIONS$find = ZOOM_OPTIONS.find(o => o.value === zoom)) == null ? void 0 : _ZOOM_OPTIONS$find.title
    }),
    id: "zoom-menu",
    menu: /*#__PURE__*/React.createElement(Menu, null, ZOOM_OPTIONS.map(option => /*#__PURE__*/React.createElement(MenuItem, {
      fontSize: 1,
      key: option.value,
      onClick: () => setZoom(option.value),
      padding: 2,
      selected: option.value === zoom,
      text: option.title
    }))),
    popover: {
      matchReferenceWidth: true
    },
    portal: true
  });
});

const WorkshopNavbar = /*#__PURE__*/memo(function WorkshopNavbar() {
  return /*#__PURE__*/React.createElement(Card, {
    borderBottom: true,
    flex: "none",
    padding: 2,
    style: {
      lineHeight: 0
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    gap: 1
  }, /*#__PURE__*/React.createElement(Box, {
    display: ['block', 'block', 'none']
  }, /*#__PURE__*/React.createElement(Button, {
    fontSize: 1,
    icon: MenuIcon,
    mode: "bleed",
    padding: 2
  })), /*#__PURE__*/React.createElement(Flex, {
    flex: 1,
    justify: ['center', 'center', 'flex-start'],
    padding: 2,
    sizing: "border",
    style: {
      minWidth: 250
    }
  }, /*#__PURE__*/React.createElement(NavbarBreadcrumbs, null)), /*#__PURE__*/React.createElement(Box, {
    display: ['block', 'block', 'none']
  }, /*#__PURE__*/React.createElement(Button, {
    fontSize: 1,
    icon: ControlsIcon,
    mode: "bleed",
    padding: 2
  })), /*#__PURE__*/React.createElement(Box, {
    display: ['none', 'none', 'block']
  }, /*#__PURE__*/React.createElement(Inline, {
    space: 1
  }, /*#__PURE__*/React.createElement(OpenCanvasButton, null), /*#__PURE__*/React.createElement(ViewportMenu, null), /*#__PURE__*/React.createElement(ZoomMenu, null), /*#__PURE__*/React.createElement(SchemeMenu, null)))));
});

function buildMenuItems(collections, node, name) {
  if (Array.isArray(node.stories)) {
    return {
      type: 'scope',
      name: node.name,
      title: node.title,
      scope: node
    };
  }

  const coll = collections.find(c => c.name === name);
  return {
    type: 'list',
    name,
    title: coll ? coll.title : name,
    items: Object.entries(node).map(([name, child]) => buildMenuItems((coll == null ? void 0 : coll.children) || [], child, name))
  };
}
function buildMenu(collections, scopes) {
  const scopeMap = {}; // Merge scopes

  for (const scope of scopes) {
    if (scopeMap[scope.name]) {
      var _scopeMap$scope$name$;

      scopeMap[scope.name] = _extends({}, scopeMap[scope.name], {
        title: scope.title,
        stories: ((_scopeMap$scope$name$ = scopeMap[scope.name].stories) == null ? void 0 : _scopeMap$scope$name$.concat(scope.stories || [])) || []
      });
    } else {
      scopeMap[scope.name] = scope;
    }
  }

  let tree = {};

  for (const [name, scope] of Object.entries(scopeMap)) {
    tree = set(tree, name, scope);
  }

  return buildMenuItems([{
    children: collections
  }], tree);
}

const SearchResults = /*#__PURE__*/memo(function SearchResults(props) {
  const {
    matches,
    onStoryClick
  } = props;
  return /*#__PURE__*/React.createElement(Stack$1, {
    space: 1
  }, matches.map(({
    scope,
    story
  }) => /*#__PURE__*/React.createElement(Card, {
    as: "a",
    "data-path": `/${scope.name}/${story.name}`,
    href: `/${scope.name}/${story.name}`,
    key: `${scope.name}/${story.name}`,
    onClick: onStoryClick,
    padding: 2,
    radius: 2
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    textOverflow: "ellipsis",
    weight: "semibold"
  }, scope.title, " / ", story.title))));
});

const StoryTree = /*#__PURE__*/memo(function StoryTree(props) {
  const {
    items
  } = props;
  return /*#__PURE__*/React.createElement(Tree, {
    space: 1
  }, /*#__PURE__*/React.createElement(MenuItems, {
    items: items
  }));
});
const MenuItems = /*#__PURE__*/memo(function MenuItems(props) {
  const {
    basePath = '',
    items
  } = props;
  const {
    broadcast,
    path: workshopPath,
    scope: currentScope,
    story: currentStory
  } = useWorkshop();
  const handleStoryClick = useCallback(event => {
    event.preventDefault();
    const target = event.currentTarget;
    const targetPath = target.getAttribute('data-path');

    if (targetPath) {
      broadcast({
        type: 'workshop/setPath',
        value: targetPath
      });
    }
  }, [broadcast]);
  return useMemo(() => {
    if (items.length === 0) {
      return null;
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, items.map((item, itemIndex) => {
      if (item.type === 'list') {
        const path = `${basePath}/${item.name}`;
        return /*#__PURE__*/React.createElement(MemoList, {
          expanded: workshopPath.startsWith(path + '/'),
          key: item.name || itemIndex,
          item: item,
          path: path
        });
      }

      if (item.type === 'scope') {
        return /*#__PURE__*/React.createElement(MemoScope, {
          currentStory: currentStory,
          expanded: item.scope === currentScope,
          item: item,
          key: item.name,
          onStoryClick: handleStoryClick
        });
      }

      return /*#__PURE__*/React.createElement(TreeItem, {
        key: itemIndex,
        text: "unknown"
      });
    }));
  }, [basePath, currentScope, currentStory, handleStoryClick, items, workshopPath]);
});
const MemoList = /*#__PURE__*/memo(function MemoList(props) {
  const {
    expanded,
    item,
    path
  } = props;
  const children = useMemo(() => /*#__PURE__*/React.createElement(MenuItems, {
    basePath: path,
    items: item.items
  }), [item, path]);
  return /*#__PURE__*/React.createElement(TreeItem, {
    expanded: expanded,
    fontSize: 1,
    padding: 2,
    text: item.title,
    weight: "semibold"
  }, children);
});
const MemoScope = /*#__PURE__*/memo(function MemoScope(props) {
  const {
    currentStory,
    expanded,
    item,
    onStoryClick
  } = props;
  const children = useMemo(() => item.scope.stories.map(story => /*#__PURE__*/React.createElement(TreeItem, {
    "data-path": `/${item.scope.name}/${story.name}`,
    fontSize: 1,
    href: `/${item.scope.name}/${story.name}`,
    key: story.name,
    onClick: onStoryClick,
    padding: 2,
    selected: currentStory === story,
    text: story.title
  })), [currentStory, item, onStoryClick]);
  return /*#__PURE__*/React.createElement(TreeItem, {
    expanded: expanded,
    fontSize: 1,
    padding: 2,
    text: item.title,
    weight: "semibold"
  }, children);
});

let _$1 = t => t,
    _t$1;
const Root = styled(Card).attrs({
  display: ['none', 'none', 'block']
})(_t$1 || (_t$1 = _$1`
  min-width: 180px;
  max-width: 300px;
`));
const flexNoneStyle = {
  flex: 'none'
};
const lineHeightNoneStyle = {
  lineHeight: 0
};
const textInputFontSize = [2, 2, 1];
const WorkshopNavigator = /*#__PURE__*/memo(function WorkshopNavigator(props) {
  const {
    collections = []
  } = props;
  const {
    broadcast,
    scopes
  } = useWorkshop();
  const menu = useMemo(() => buildMenu(collections, scopes), [collections, scopes]);
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    if (!query) return EMPTY_ARRAY;
    const q = query.toLowerCase();
    const ret = [];

    for (const scope of scopes) {
      for (const story of scope.stories) {
        if (scope.title.toLowerCase().includes(q) || story.title.toLowerCase().includes(q)) {
          ret.push({
            scope,
            story
          });
        }
      }
    }

    return ret;
  }, [query, scopes]);
  const handleSearchQueryChange = useCallback(event => setQuery(event.currentTarget.value), []);
  const handleSearchQueryClear = useCallback(() => setQuery(''), []);
  const handleStoryClick = useCallback(event => {
    event.preventDefault();
    const target = event.currentTarget;
    const targetPath = target.getAttribute('data-path');

    if (targetPath) {
      broadcast({
        type: 'workshop/setPath',
        value: targetPath
      });
      setQuery('');
    }
  }, [broadcast]);
  return /*#__PURE__*/React.createElement(NavigatorView, {
    matches: matches,
    menu: menu,
    onSearchQueryChange: handleSearchQueryChange,
    onSearchQueryClear: handleSearchQueryClear,
    onStoryClick: handleStoryClick,
    query: query
  });
});
const NavigatorView = /*#__PURE__*/memo(function NavigatorView(props) {
  const {
    matches,
    menu,
    onSearchQueryChange,
    onSearchQueryClear,
    onStoryClick,
    query
  } = props;
  return /*#__PURE__*/React.createElement(Root, {
    borderRight: true,
    flex: 1,
    overflow: "hidden"
  }, /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    height: "fill"
  }, /*#__PURE__*/React.createElement(Layer, {
    style: flexNoneStyle
  }, /*#__PURE__*/React.createElement(Card, {
    padding: 2,
    shadow: 1,
    style: lineHeightNoneStyle
  }, /*#__PURE__*/React.createElement(TextInput, {
    border: false,
    clearButton: Boolean(query),
    fontSize: textInputFontSize,
    icon: SearchIcon,
    onChange: onSearchQueryChange,
    onClear: onSearchQueryClear,
    padding: 2,
    placeholder: "Stories",
    radius: 2,
    space: 2,
    value: query
  }))), /*#__PURE__*/React.createElement(Card, {
    flex: 1,
    overflow: "auto"
  }, query && matches.length > 0 && /*#__PURE__*/React.createElement(Box, {
    padding: 2
  }, /*#__PURE__*/React.createElement(SearchResults, {
    matches: matches,
    onStoryClick: onStoryClick
  })), !query && menu.type === 'list' && /*#__PURE__*/React.createElement(Box, {
    padding: 2
  }, /*#__PURE__*/React.createElement(StoryTree, {
    items: menu.items
  })))));
});

let _ = t => t,
    _t;
const Frame = styled.iframe(_t || (_t = _`
  display: block;
  border: 0;
  height: 100%;
  width: 100%;
`));
const WorkshopCanvas = /*#__PURE__*/memo(function WorkshopCanvas(props) {
  const {
    frameRef
  } = props;
  const {
    frameReady,
    frameUrl,
    path,
    payload,
    scheme,
    title,
    viewport,
    zoom
  } = useWorkshop();
  const viewportOption = VIEWPORT_OPTIONS.find(o => o.name === viewport) || VIEWPORT_OPTIONS[0];
  const viewportW = viewportOption == null ? void 0 : viewportOption.rect.width;
  const viewportH = viewportOption == null ? void 0 : viewportOption.rect.height;
  const [initialFrameUrl] = useState(() => buildFrameUrl({
    baseUrl: frameUrl,
    path,
    payload,
    scheme,
    viewport,
    zoom
  }));
  const containerStyle = useMemo(() => ({
    maxWidth: viewportW === 'auto' ? undefined : `${(viewportW || 1) * zoom}px`,
    maxHeight: viewportH ? `${(viewportH || 1) * zoom}px` : undefined
  }), [viewportW, viewportH, zoom]);
  const frameStyle = useMemo(() => ({
    transform: `scale(${zoom})`,
    transformOrigin: '0 0',
    width: `${100 / zoom}%`,
    height: `${100 / zoom}%`
  }), [zoom]);
  return /*#__PURE__*/React.createElement(Card, {
    flex: 1,
    overflow: "hidden",
    tone: "transparent"
  }, /*#__PURE__*/React.createElement(Flex, {
    align: "center",
    height: "fill",
    justify: "center",
    sizing: "border"
  }, path === '/' && /*#__PURE__*/React.createElement(Container, {
    width: 0
  }, /*#__PURE__*/React.createElement(Stack$1, {
    padding: 4,
    space: 4
  }, /*#__PURE__*/React.createElement(Heading, {
    align: "center"
  }, title), /*#__PURE__*/React.createElement(Text, {
    align: "center",
    muted: true
  }, "Browse workshop stories in the navigator to the left."))), !frameReady && path !== '/' && /*#__PURE__*/React.createElement(Spinner, {
    muted: true
  }), /*#__PURE__*/React.createElement(Container, {
    height: "fill",
    hidden: !frameReady || path === '/',
    style: containerStyle,
    width: "auto"
  }, /*#__PURE__*/React.createElement(Card, {
    height: "fill",
    shadow: 1
  }, /*#__PURE__*/React.createElement(Frame, {
    ref: frameRef,
    src: initialFrameUrl,
    style: frameStyle
  })))));
});

function createWorkshopFrameController() {
  const _subscribers = new Set();

  let _frameElement = null;
  let _msgQueue = [];
  let _flushTimeout = null;

  function _flush() {
    if (_flushTimeout) {
      clearInterval(_flushTimeout);
    }

    _flushTimeout = setTimeout(() => {
      var _frameElement2, _frameElement2$conten;

      (_frameElement2 = _frameElement) == null ? void 0 : (_frameElement2$conten = _frameElement2.contentWindow) == null ? void 0 : _frameElement2$conten.postMessage(_msgQueue, window.location.origin);
      _msgQueue = [];
      _flushTimeout = null;
    }, 0);
  }

  function _handleMessage(event) {
    const msgs = event.data;

    if (isArray(msgs)) {
      for (const msg of msgs) {
        if (isRecord(msg) && typeof msg.type === 'string' && msg.type.startsWith('workshop/')) {
          for (const subscriber of _subscribers) {
            subscriber(msg);
          }
        }
      }
    }
  }

  function _mount() {
    var _frameElement3;

    if ((_frameElement3 = _frameElement) != null && _frameElement3.contentWindow) {
      window.addEventListener('message', _handleMessage);
    }
  }

  function _unmount(el) {
    if (el != null && el.contentWindow) {
      window.removeEventListener('message', _handleMessage);
    }
  }

  return {
    message: {
      publish(msg) {
        _msgQueue.push(msg);

        _flush();
      },

      subscribe(subscriber) {
        _subscribers.add(subscriber);

        return () => {
          _subscribers.delete(subscriber);
        };
      }

    },

    setElement(el) {
      const prevFrameElement = _frameElement;
      _frameElement = el;

      if (el) {
        _mount();
      } else if (prevFrameElement) {
        _unmount(prevFrameElement);
      }
    }

  };
}

const _excluded = ["scheme", "viewport", "zoom"];

function getStateFromLocation(loc, schemeProp, frameReady) {
  const path = loc.path;
  const query = loc.query || {};

  const {
    scheme,
    viewport,
    zoom
  } = query,
        payload = _objectWithoutPropertiesLoose(query, _excluded);

  return {
    frameReady: frameReady || false,
    path,
    payload,
    scheme: schemeProp || (typeof scheme === 'string' ? scheme : 'light'),
    viewport: typeof viewport === 'string' ? viewport : 'auto',
    zoom: typeof zoom === 'number' ? zoom : 1
  };
}

function getQueryFromState(state, withPayload = true) {
  const {
    payload,
    scheme,
    viewport,
    zoom
  } = state;
  const query = {
    scheme
  };

  if (viewport && viewport !== DEFAULT_VIEWPORT_VALUE) {
    query.viewport = viewport;
  }

  if (zoom && zoom !== DEFAULT_ZOOM_VALUE) {
    query.zoom = zoom;
  }

  if (withPayload) {
    for (const [key, val] of Object.entries(payload)) {
      if (['schema', 'viewport', 'zoom'].includes(key)) {
        // eslint-disable-next-line no-console
        console.warn(`Workshop: the payload cannot contain a property named "${key}" (protected name)`);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        query[key] = val;
      }
    }
  }

  return query;
}

const Workshop = /*#__PURE__*/memo(function Workshop(props) {
  var _config$features$navb, _config$features;

  const {
    config,
    locationStore,
    onSchemeChange,
    scheme: schemeProp
  } = props;
  const withNavbar = (_config$features$navb = (_config$features = config.features) == null ? void 0 : _config$features.navbar) != null ? _config$features$navb : true;
  const channel = useMemo(() => createPubsub(), []);
  const frame = useMemo(() => createWorkshopFrameController(), []);
  const [boundaryElement, setBoundaryElement] = useState(null);
  const [portalElement, setPortalElement] = useState(null);
  const [{
    frameReady,
    path,
    payload,
    scheme,
    viewport,
    zoom
  }, setState] = useState(() => getStateFromLocation(locationStore.get(), schemeProp));
  const frameReadyRef = useRef(frameReady);
  const schemeRef = useRef(scheme);
  const pathRef = useRef(path);
  const queryRef = useRef(_extends({
    scheme,
    viewport,
    zoom
  }, payload));
  const broadcast = useCallback(msg => {
    // Handle msg
    channel.publish(msg); // Pass message to frame

    frame.message.publish(msg);
  }, [channel, frame]);

  const _pushLocation = useMemo(() => debounce_1(loc => locationStore.push(loc), 150), [locationStore]);

  const _replaceLocation = useMemo(() => debounce_1(loc => locationStore.replace(loc), 150), [locationStore]); // Cancel debounced functions


  useEffect(() => () => _pushLocation.cancel(), [_pushLocation]);
  useEffect(() => () => _replaceLocation.cancel(), [_replaceLocation]); // Subscribe to global message channel

  useEffect(() => channel.subscribe(msg => {
    // Update state
    setState(prevState => {
      const nextState = workshopReducer(prevState, msg);
      const changed = !isEqual_1(prevState, nextState);

      if (changed) {
        // Update URL location
        if (msg.type === 'workshop/setPath') {
          if (pathRef.current !== nextState.path) {
            pathRef.current = nextState.path; // query without payload

            const nextQuery = getQueryFromState(nextState, false);

            _pushLocation({
              path: nextState.path,
              query: nextQuery
            });
          }
        } else if (msg.type !== 'workshop/setState') {
          // query with payload
          const nextQuery = getQueryFromState(nextState);

          if (!isEqual_1(queryRef.current, nextQuery)) {
            queryRef.current = nextQuery;

            _replaceLocation({
              path: nextState.path,
              query: nextQuery
            });
          }
        }
      }

      return changed ? nextState : prevState;
    });
  }), [_pushLocation, _replaceLocation, channel, locationStore]); // Pipe messages from frame to channel

  useEffect(() => frame.message.subscribe(channel.publish), [channel, frame]);
  useEffect(() => {
    frameReadyRef.current = frameReady;
  }, [frameReady]); // Handle location updates

  useEffect(() => locationStore.subscribe(loc => {
    const nextState = getStateFromLocation(loc, undefined, frameReady);
    broadcast({
      type: 'workshop/setState',
      value: nextState
    });
  }), [broadcast, frameReady, locationStore]); // Observe `scheme` state

  useEffect(() => {
    schemeRef.current = scheme; // Call the `onSchemeChange` callback when `scheme` changes

    onSchemeChange(scheme);
  }, [onSchemeChange, scheme]); // Broadcast changes to `scheme` property

  useEffect(() => {
    if (schemeProp) {
      if (schemeRef.current !== schemeProp) {
        schemeRef.current = schemeProp;
        broadcast({
          type: 'workshop/setScheme',
          value: schemeProp
        });
      }
    }
  }, [broadcast, schemeProp]);

  if (!config.scopes) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, "No scopes");
  }

  return /*#__PURE__*/React.createElement(WorkshopProvider, {
    config: config,
    broadcast: broadcast,
    channel: channel,
    frameReady: frameReady,
    origin: "main",
    path: path,
    payload: payload,
    scheme: scheme,
    viewport: viewport,
    zoom: zoom
  }, /*#__PURE__*/React.createElement(ToastProvider, null, /*#__PURE__*/React.createElement(BoundaryElementProvider, {
    element: boundaryElement
  }, /*#__PURE__*/React.createElement(PortalProvider, {
    element: portalElement
  }, /*#__PURE__*/React.createElement(Flex, {
    "data-boundary": "",
    direction: "column",
    height: "fill",
    ref: setBoundaryElement
  }, withNavbar && /*#__PURE__*/React.createElement(WorkshopNavbar, null), /*#__PURE__*/React.createElement(Flex, {
    flex: 1
  }, /*#__PURE__*/React.createElement(WorkshopNavigator, {
    collections: config.collections
  }), /*#__PURE__*/React.createElement(WorkshopCanvas, {
    frameRef: frame.setElement
  }), /*#__PURE__*/React.createElement(WorkshopInspector, null)), /*#__PURE__*/React.createElement("div", {
    "data-portal": "",
    ref: setPortalElement
  }))))));
});

function useAction(name) {
  const {
    scope,
    story
  } = useWorkshop();
  return useCallback((...args) => {
    if (!scope || !story) return; // eslint-disable-next-line no-console

    console.log(`[${scope.name}/${story.name}]`, name, ...args);
  }, [scope, story, name]);
}

export { DEFAULT_VIEWPORT_VALUE, DEFAULT_ZOOM_VALUE, EMPTY_ARRAY, EMPTY_RECORD, VIEWPORT_OPTIONS, Workshop, WorkshopFrame, WorkshopProvider, ZOOM_OPTIONS, a11yPlugin, createLocationStore, defineConfig, defineScope, perfPlugin, propsPlugin, useAction, useBoolean, useNumber, usePerf, usePerfTest, useProps, useSelect, useString, useText, useWorkshop, workshopReducer };
//# sourceMappingURL=sanity-ui-workshop.modern.js.map
